import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaCaretDown } from 'react-icons/fa';
import { theme } from '../../styles/theme';

const Nav = styled(motion.nav).withConfig({
  shouldForwardProp: (prop) => !['$scrolled'].includes(prop),
})`
  background: ${({ $scrolled }) => ($scrolled ? theme.colors.primary : 'transparent')};
  height: 80px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1rem;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: ${theme.zIndex.navbar};
  transition: all ${theme.transitions.medium};
  box-shadow: ${({ $scrolled }) => ($scrolled ? theme.shadows.medium : 'none')};
  backdrop-filter: ${({ $scrolled }) => ($scrolled ? 'blur(8px)' : 'none')};
`;

const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 80px;
  max-width: 1200px;
  padding: 0 ${theme.spacing.md};
`;

const MotionLink = motion(Link);
const NavLogo = styled(MotionLink)`
  color: ${theme.colors.text.primary};
  justify-self: flex-start;
  cursor: pointer;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  font-weight: ${theme.typography.fontWeight.bold};
  text-decoration: none;
  position: relative;
  z-index: ${theme.zIndex.navbar + 1};
  
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
    border-radius: ${theme.borderRadius.small};
  }
`;

const MobileIcon = styled.button`
  display: none;
  background: transparent;
  border: none;
  padding: ${theme.spacing.xs};
  cursor: pointer;
  color: ${theme.colors.text.primary};
  z-index: ${theme.zIndex.navbar + 1};
  
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
    border-radius: ${theme.borderRadius.small};
  }
  
  @media screen and (max-width: 768px) {
    display: block;
    position: absolute;
    top: 0;
    right: 0;
    transform: translate(-100%, 60%);
    font-size: 1.8rem;
  }
`;

const NavMenu = styled(motion.ul).withConfig({
  shouldForwardProp: (prop) => !['$isOpen'].includes(prop),
})`
  display: flex;
  align-items: center;
  list-style: none;
  text-align: center;
  margin-right: -22px;
  
  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 90vh;
    position: absolute;
    top: ${({ $isOpen }) => ($isOpen ? '80px' : '-1000px')};
    left: 0;
    opacity: 1;
    transition: all ${theme.transitions.medium};
    background: ${theme.colors.primary};
    box-shadow: ${theme.shadows.large};
  }
`;

const NavItem = styled(motion.li)`
  height: 80px;
  
  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;

const NavLinks = styled(MotionLink)`
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 0 1rem;
  height: 100%;
  cursor: pointer;
  position: relative;
  font-weight: ${theme.typography.fontWeight.medium};
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: 20px;
    left: 50%;
    background-color: ${theme.colors.secondary};
    transform: translateX(-50%);
    transition: width ${theme.transitions.medium};
  }
  
  &:hover:after,
  &:focus:after {
    width: 60%;
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
    border-radius: ${theme.borderRadius.small};
  }
  
  @media screen and (max-width: 768px) {
    text-align: center;
    padding: 2rem;
    width: 100%;
    display: table;
    
    &:hover,
    &:focus {
      color: ${theme.colors.secondary};
      transition: all ${theme.transitions.medium};
    }
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`;

const DropdownButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  font-size: 1rem;
  cursor: pointer;
  padding: 0 1rem;
  height: 100%;
  font-family: inherit;
  font-weight: ${theme.typography.fontWeight.medium};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: 20px;
    left: 50%;
    background-color: ${theme.colors.secondary};
    transform: translateX(-50%);
    transition: width ${theme.transitions.medium};
  }
  
  &:hover:after,
  &:focus:after {
    width: 60%;
  }
  
  &:focus-visible {
    outline: none;
  }
`;

const DropdownContent = styled.div`
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  position: absolute;
  top: 80px;
  background: ${theme.colors.primary};
  min-width: 250px;
  flex-direction: column;
  box-shadow: ${theme.shadows.medium};
  z-index: 100;
  border-radius: ${theme.borderRadius.small};
  padding: 0.5rem 0;
  border: 1px solid ${theme.colors.border};
`;

const DropdownLink = styled(Link)`
  color: ${theme.colors.text.primary};
  padding: 0.75rem 1.5rem;
  text-decoration: none;
  display: block;
  transition: all ${theme.transitions.fast};
  font-weight: ${theme.typography.fontWeight.medium};
  
  &:hover {
    background-color: ${theme.colors.background.light};
    color: ${theme.colors.secondary};
  }
`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggle = () => setIsOpen(!isOpen);
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);
  
  useEffect(() => {
    const changeNav = () => {
      if (window.scrollY >= 80) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', changeNav);
    
    return () => {
      window.removeEventListener('scroll', changeNav);
    };
  }, []);

  // Animation variants
  const navVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: {
        delay: 0.2,
        duration: 0.6
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  return (
    <>
      <Nav 
        $scrolled={scrolled}
        initial="hidden"
        animate="visible"
        variants={navVariants}
      >
        <NavbarContainer>
          <NavLogo 
            to="/"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Afrotech Group Home"
          >
            Afrotech Group
          </NavLogo>
          <MobileIcon 
            onClick={toggle}
            aria-label={isOpen ? "Close menu" : "Open menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            <AnimatePresence mode="wait" initial={false}>
              {isOpen ? (
                <motion.div
                  key="close"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaTimes />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <FaBars />
                </motion.div>
              )}
            </AnimatePresence>
          </MobileIcon>
          <NavMenu 
            $isOpen={isOpen}
            id="mobile-menu"
            role="navigation"
            aria-label="Main navigation"
          >
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Home
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/about" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                About Us
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/solutions" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Solutions
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/products" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Products
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/investments" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Investments
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/blog" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Blog
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <DropdownContainer className="dropdown-container">
                <DropdownButton onClick={toggleDropdown}>
                  Services <FaCaretDown style={{ marginLeft: '5px' }} />
                </DropdownButton>
                <DropdownContent $isOpen={dropdownOpen}>
                  <DropdownLink to="/services/website-creation-pretoria" onClick={() => { setDropdownOpen(false); toggle(); }}>Website Creation Pretoria</DropdownLink>
                  <DropdownLink to="/services/app-development-south-africa" onClick={() => { setDropdownOpen(false); toggle(); }}>App Development South Africa</DropdownLink>
                  <DropdownLink to="/services/software-development" onClick={() => { setDropdownOpen(false); toggle(); }}>Software Development</DropdownLink>
                  <DropdownLink to="/services/data-development" onClick={() => { setDropdownOpen(false); toggle(); }}>Data Development</DropdownLink>
                  <DropdownLink to="/pretoria-web-design" onClick={() => { setDropdownOpen(false); toggle(); }}>Pretoria Web Design</DropdownLink>
                </DropdownContent>
              </DropdownContainer>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/partners" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Partners
              </NavLinks>
            </NavItem>
            <NavItem variants={itemVariants}>
              <NavLinks 
                to="/contact#contact-form" 
                onClick={toggle}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Contact
              </NavLinks>
            </NavItem>
          </NavMenu>
        </NavbarContainer>
      </Nav>
    </>
  );
};

export default Navbar;
